// src/pages/Cases.jsx
import DelBox from '../comps/DelBox';
import AddCase from '../models/AddCase';
import { useEffect, useMemo, useRef, useState } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import Swal from "sweetalert2";

const Cases = () => {
  const [formData, setFormData] = useState({
    l1: [],
    cat_type: 'case',
    page_no: 0,
    alertMsg: '',
    alertMsgTyp: '',
    search_key: '',
    isAlert: false,
  });
  const [ldata, setLdata] = useState(null);
  const [editItem, setEditItem] = useState(null);

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // local loading
  const [loading, setLoading] = useState(false);

  // fetch list
  const initData = async () => {
    setLoading(true);
    const storedData = sessionStorage.getItem("ldata");
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setLdata(parsed);

      const fd = new FormData();
      fd.append('cat_type', formData.cat_type);
      fd.append('page_no', formData.page_no);
      fd.append('search_key', formData.search_key);
      fd.append('par_by', parsed.user_id || 1);

      try {
        const response = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.status) {
          const records = response.data.records || [];
          setFormData(prev => ({ ...prev, l1: records }));
        } else {
          setFormData(prev => ({ ...prev, l1: [] }));
        }
      } catch (error) {
        console.error("Failed to fetch case list:", error);
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // re-load when search_key changes (debounce)
    const t = setTimeout(() => initData(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.search_key]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const delItem = async (row) => {
    if (!row?.cat_id) return;
    const confirm = window.confirm("Are you sure you want to delete this case?");
    if (!confirm) return;

    const fd = new FormData();
    fd.append('cat_id', row.cat_id);
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.delCatUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.status) {
        initData();
        toast.success('Deleted successfully');
      } else {
        toast.error(response.data.success || "Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Delete failed");
    }
  };

  const handleAddCase = () => {
    setFormData(prev => ({ ...prev, page_no: 0 }));
    initData();
  };

  // fetch details by id
  const fetchCaseDetails = async (cat_id) => {
    const fd = new FormData();
    fd.append('cat_id', cat_id);
    const url = AppConst.BASE_URL + (AppConst.getCatDetailsUrl || AppConst.getCatListUrl);
    const res = await axios.post(url, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // backend expected shapes:
    if (res.data?.record) return res.data.record;
    if (Array.isArray(res.data?.records)) return res.data.records[0] || null;
    return null;
  };

  const openDetails = async (item) => {
    setLoadingDetails(true);
    setDetailsItem(null);
  
    try {
      const full = await fetchCaseDetails(item.cat_id);
      const resolved = full || item;
      setDetailsItem(resolved);
  
      // show bootstrap modal programmatically
      const modalEl = document.getElementById('showModalCaseDetails');
      if (modalEl && window.bootstrap) {
        // create or get existing instance and show
        let instance = window.bootstrap.Modal.getInstance(modalEl);
        if (!instance) instance = new window.bootstrap.Modal(modalEl, { backdrop: true });
        instance.show();
      } else {
        console.warn('Bootstrap JS not loaded or modal element not found.');
      }
    } catch (e) {
      console.error("Details fetch error", e);
      // fallback: still try to open modal with row data
      setDetailsItem(item);
      const modalEl = document.getElementById('showModalCaseDetails');
      if (modalEl && window.bootstrap) {
        let instance = window.bootstrap.Modal.getInstance(modalEl);
        if (!instance) instance = new window.bootstrap.Modal(modalEl, { backdrop: true });
        instance.show();
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  

  const closeDetails = () => {
    const modalEl = document.getElementById('showModalCaseDetails');
    if (modalEl && window.bootstrap) {
      const instance = window.bootstrap.Modal.getInstance(modalEl);
      if (instance) instance.hide();
    }
    setTimeout(() => setDetailsItem(null), 300);
  };
  

  // helper to safely show HTML or placeholder
  const htmlOrEmpty = (html) =>
    html && String(html).trim().length
      ? { __html: html }
      : { __html: "<i class='text-muted'>Not provided</i>" };

  // DataTable columns (react-data-table-component)
  const columns = useMemo(() => [
    {
      name: 'SN',
      selector: (row, idx) => idx + 1,
      width: '70px',
    },
    {
      name: 'Case Name',
      selector: row => row.cat_name || '',
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setEditItem(row);
              document.getElementById("addcasebtn")?.click();
            }}
          >
            <i className="ri-pencil-fill me-1"></i> Edit
          </button>

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openDetails(row)}
          >
            <i className="ri-file-list-line me-1"></i> More
          </button>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => delItem(row)}
          >
            <i className="ri-delete-bin-5-fill me-1"></i> Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '280px',
    }
  ], []);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#f1f5fb', // EmpList header bg
        color: '#405189',           // EmpList header text
        fontSize: '14px',
        fontWeight: '700',
      }
    },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight: '12px' } }
  };

  // filtered rows (client-side)
  const filteredList = (formData.l1 || []).filter(item => {
    if (!formData.search_key || formData.search_key.trim() === "") return true;
    return item.cat_name?.toLowerCase().includes(formData.search_key.toLowerCase());
  });

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="orderList">
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Case List</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {/* Hidden trigger for AddCase modal */}
                      <button id="addcasebtn" style={{ display: "none" }} className="btn btn-success" data-bs-toggle="modal" data-bs-target="#showModalCase"></button>

                      <button
                        className="btn btn-success"
                        onClick={(e) => {
                          e.preventDefault();
                          setEditItem(null);
                          document.getElementById("addcasebtn")?.click();
                        }}
                      >
                        <i className="ri-add-line me-1"></i> Add Case
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body border-top">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="row g-3">
                    <div className="col-xxl-5 col-sm-6">
                      <div className="search-box">
                        <input
                          className="form-control"
                          name="search_key"
                          onChange={handleChange}
                          value={formData.search_key}
                          placeholder="Search by case name..."
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="card-body pt-0">
                <div className="table-responsive table-card mb-1">
                  <DataTable
                    columns={columns}
                    data={filteredList}
                    customStyles={customStyles}
                    noHeader
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 50]}
                    highlightOnHover
                    persistTableHead
                    progressPending={loading}
                    pointerOnHover
                  />
                </div>

                <AddCase item={editItem} onAddCase={handleAddCase} />
                <DelBox />

                {/* Details Modal */}
                <div className="modal fade" id="showModalCaseDetails" tabIndex="-1" aria-hidden={!showDetailsModal}>
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Case Details</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={closeDetails}></button>
                      </div>
                      <div className="modal-body">
                        {!detailsItem ? (
                          loadingDetails
                            ? <p className="text-muted">Loading details...</p>
                            : <p className="text-muted">No details available.</p>
                        ) : (
                          <>
                            <h6 className="mb-3">Case: {detailsItem.cat_name}</h6>
                            <hr />

                            <div className="mb-3">
                              <label className="form-label"><strong>DIAGNOSIS</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.diagnosis)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>PROCEDURE</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.procedure)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>COURSE IN HOSPITAL</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.course_in_hospital)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>NOTES</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.notes)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>CLINICAL HISTORY</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.clinical_history)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>PHYSICAL EXAMINATION</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.physical_examination)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>GENERAL EXAMINATION</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.general_examination)} />
                            </div>

                            <div className="mb-3">
                              <label className="form-label"><strong>SYSTEMATIC EXAMINATION</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.systematic_examination)} />
                            </div>

                            <div className="mb-2">
                              <label className="form-label"><strong>ADVICE</strong></label>
                              <div className="p-2 border rounded" style={{ minHeight: 40 }}
                                   dangerouslySetInnerHTML={htmlOrEmpty(detailsItem.advice)} />
                            </div>
                          </>
                        )}
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={closeDetails}>Close</button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cases;
